import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { clearListingFirst, firstRunDoneHere, firstRunOwed, letInForListing, needsFirstRun, skipFirstRunForNow } from '../lib/v2'
import { ScreenLoading } from './Spinner'
import { dropEarlyBrowse } from '../lib/earlyBrowse'

/**
 * A screen behind the first run (V2: Browse, and Create a plan for Make your own). Returning people (done here or on the
 * server) go straight in. `?skip=1` (the first run's way out when the session cannot start, review H2) lets this
 * browser session through. Off `/`, the first run is told where to come back to (`?next=`, review M7). Listing first
 * (alvin 10/17): a shared listing link (`/?item=`, `/listing/:id`) opens that listing at once for a new visitor too; the
 * first run waits for the first Save or Build around this (ListingActions) and comes back to the listing.
 */
export function FirstRunGate({ children }: { children: ReactNode }) {
  const loc = useLocation()
  const skip = new URLSearchParams(loc.search).get('skip') === '1'
  if (skip) skipFirstRunForNow()
  const [need, setNeed] = useState<boolean | null | undefined>(firstRunDoneHere() || skip ? false : undefined)
  // once let in by a listing link, this visit stays in Browse (closing the sheet does not throw them into the first run)
  // The first run counts as owed from the first paint (a tap on Save right after the sheet appears must not slip past it
  // while the server check is still out); the flag is cleared if the check says it is not needed (gate race, 10/17).
  const [listing] = useState(() => {
    const on = new URLSearchParams(loc.search).has('item') || firstRunOwed()
    if (on && !firstRunDoneHere() && !skip) letInForListing()
    return on
  })
  useEffect(() => {
    if (need === false) return
    let live = true
    void needsFirstRun().then((n) => { if (!live) return; if (!n) clearListingFirst(); setNeed(n) })
    return () => { live = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  if (listing && need !== false) return <>{children}</>
  if (need === undefined) return <ScreenLoading />
  if (need) {
    // F5: a Browse answer index.html fetched early was for the person before this first run; it must never be shown
    // after it (lib/earlyBrowse.ts), so it is dropped here
    dropEarlyBrowse()
    const search = loc.pathname === '/' ? loc.search : `?next=${encodeURIComponent(loc.pathname + loc.search)}`
    return <Navigate to={{ pathname: '/first-run', search }} replace />
  }
  return <>{children}</>
}
